import React, { useEffect, useState } from 'react';

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import AddCalendarMonthIcon from 'icons/calendar-month.svg';
import AddImportantDevicesIcon from 'icons/important-devices.svg';
import _ from 'lodash';
import moment from 'moment';
import { Trans, useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { z } from 'zod';

import { useAttachmentsBulkProcessingStatusQuery } from 'components/hooks/attachmentsBulkProcessingStatusQueries';
import useDebounce from 'components/hooks/useDebounce';
import useForm from 'components/hooks/useForm';
import useModal from 'components/hooks/useModal';
import useRequestError from 'components/hooks/useRequestError';
import useSidePanel from 'components/hooks/useSidePanel';
import useValidatedStore from 'components/hooks/useValidatedStore';
import useWindowStorage from 'components/hooks/useWindowStorage';

import Paginator from 'components/application/Paginator';
import ResourceBlankNotice from 'components/application/ResourceBlankNotice';
import SuccessToast from 'components/application/SuccessToast';

import BlankBookingSearchResult from 'components/bookings/BlankBookingSearchResult';
import BookingConnectionTrainingSidePanel from 'components/bookings/BookingConnectionTrainingSidePanel';
import BookingsBar from 'components/bookings/BookingsBar';
import BookingSourceReminderModal from 'components/bookings/BookingSourceReminderModal';
import BookingsTable from 'components/bookings/BookingsTable';
import DestroyBookingSourceModal from 'components/bookings/DestroyBookingSourceModal';
import SendBookingConnectionUpdatedNotificationModal from 'components/bookings/SendBookingConnectionUpdatedNotificationModal';
import { useCurrentActor } from 'components/contexts/CurrentActor';
import { TabContext } from 'components/contexts/TabContext';
import { useBreadBoard } from 'components/contexts/Toaster';
import BookingSidePanel from 'components/training/BookingSidePanel';

import { formatDate } from 'components/helpers/dates';
import {
  collectData,
  formatInfiniteData,
  getNextPageParam,
} from 'components/helpers/reactQuery';
import {
  calculateTrainingExpiryDate,
  defaultTrainingStartDate,
  inferredTrainingExpiryDates,
} from 'components/helpers/resources/training';

const initialTabStore = {
  currentSearch: '',
  currentTabPages: {
    eLearning: '1',
    pastBookings: '1',
    upcomingBookings: '1',
  },
};

const tabSchema = z.object({
  currentSearch: z.string(),
  currentTabPages: z.object({
    eLearning: z.string().or(z.null()),
    pastBookings: z.string().or(z.null()),
    upcomingBookings: z.string().or(z.null()),
  }),
  selectedTabKey: z.string(),
});

const defaultTabs = [
  {
    key: 'upcomingBookings',
    icon: (
      <AddCalendarMonthIcon
        className='tw-mb-2 [&_path]:tw-fill-grey-300'
        height={64}
        width={64}
      />
    ),
    param: 'upcoming_bookings',
  },
  {
    key: 'pastBookings',
    icon: (
      <AddCalendarMonthIcon
        className='tw-mb-2 [&_path]:tw-fill-grey-300'
        height={64}
        width={64}
      />
    ),
    param: 'past_bookings',
  },
];

const eLearningTab = {
  key: 'eLearning',
  icon: (
    <AddImportantDevicesIcon
      className='tw-mb-2 [&_path]:tw-fill-grey-300'
      height={64}
      width={64}
    />
  ),
  param: 'e_learning',
};

const defaultBookingConnection = {
  id: null,
  date: null,
  notes: '',
  personnelIds: [],
};

const defaultBookingConnectionTraining = {
  expiryDate: null,
  notes: '',
  personnelIds: [],
  startDate: null,
  attachmentIds: [],
};

// TODO: probably remove these  -----

function findCourseFromCache(queryClient, courseId) {
  return collectData({
    queryClient,
    queryKey: ['bookings', 'assignableCourses'],
  }).find((course) => course.id === courseId.toString());
}

function findELearningCourseFromCache(queryClient, id) {
  const queryData = queryClient.getQueriesData([
    'bookings',
    'assignableCourses',
  ]);
  const flattened = queryData
    .flatMap(([_key, data]) => data?.included || [])
    .flat();
  return flattened.find(
    (inclusion) => inclusion.type === 'eLearningCourse' && inclusion.id === id,
  );
}
// ---------------------- c

export default function BookingsTab() {
  // utilities
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();
  const queryClient = useQueryClient();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'bookings.BookingsTab',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const [endOfDomainPersonnelListRef, isEndOfDomainPersonnelListRefInView] =
    useInView();

  // local storage
  const [getStore, setStore] = useWindowStorage('trainingRegister|bookings', {
    store: window.sessionStorage,
  });

  const tabStore = useValidatedStore({
    getStore,
    initialStore: initialTabStore,
    schema: tabSchema,
  });

  // searching
  const [currentSearch, setCurrentSearch, handleSearchInputChange] = useForm({
    bookingSearch: tabStore.currentSearch,
  });

  const [debouncedCurrentSearch, _resetDebouncedCurrentSearch] = useDebounce(
    currentSearch,
    250,
  );

  // request
  const [
    requestError,
    submitDisabled,
    removeErrorStyling,
    resetRequestError,
    handleRequestError,
  ] = useRequestError();

  // sidepanels
  const [
    bookingSidePanelIsOpen,
    _setBookingSidePanelIsOpen,
    openBookingSidePanel,
    closeBookingSidePanel,
    resetBookingSidePanelContext,
    bookingSidePanelContext,
    setBookingSidePanelContext,
  ] = useSidePanel(false, 'show');

  const [
    bookingConnectionTrainingSidePanelIsOpen,
    _setbookingConnectionTrainingSidePanelIsOpen,
    openBookingConnectionTrainingSidePanel,
    closeBookingConnectionTrainingSidePanel,
  ] = useSidePanel(false, 'show');

  // selections
  let tabs = defaultTabs;

  if (currentActor.isAllowedFeature('e_learning')) {
    tabs = tabs.concat([eLearningTab]);
  }

  const [selectedBookingConnectionId, setSelectedBookingConnectionId] =
    useState(null);

  const [selectedBookingConnectionsPage, setSelectedBookingConnectionsPage] =
    useState(tabStore.currentTabPages);

  const [selectedCourseId, setSelectedCourseId] = useState(null);

  const [selectedTabKey, setSelectedTabKey] = useState(
    (
      tabStore.selectedTabKey &&
        tabs.find((selectedTab) => selectedTab.key === tabStore.selectedTabKey)
    ) ?
      tabStore.selectedTabKey
    : tabs[0].key,
  );

  // modals
  const [
    bookingSourceReminderModalIsOpen,
    ,
    openBookingSourceReminderModal,
    closeBookingSourceReminderModal,
  ] = useModal(false);

  const [
    isRemoveBookingConnectionModalOpen,
    setIsRemoveBookingConnectionModalOpen,
  ] = useState(false);

  const [
    isSendBookingConnectionUpdatedNotificationModalOpen,
    setIsSendBookingConnectionUpdatedNotificationModalOpen,
  ] = useState(false);

  // form state
  const [
    currentBookingConnectionTraining,
    setCurrentBookingConnectionTraining,
    handleBookingConnectionTrainingInputChange,
    ,
    ,
    handleBookingConnectionTrainingDateChange,
  ] = useForm(defaultBookingConnectionTraining);

  const [
    currentBookingConnection,
    setCurrentBookingConnection,
    handleBookingConnectionInputChange,
    ,
    ,
    handleBookingConnectionDateChange,
  ] = useForm(defaultBookingConnection);

  // query computed values
  const selectedTab = tabs.find((tab) => tab.key === selectedTabKey);

  const filters = {
    page: selectedBookingConnectionsPage[selectedTabKey] || 1,
    tabParam: selectedTab.param,
    search: debouncedCurrentSearch.bookingSearch,
  };

  const tabKeys = tabs.map((tab) => tab.key);

  const tabKeysAndNames = {
    ...i18nT('tabs', { returnObjects: true }),
    eLearning: i18nTCommon('courses.learningMethods.eLearning'),
  };

  const tabNames = tabKeys.map((tabKey) => tabKeysAndNames[tabKey]);

  const {
    data: bookingConnections,
    isSuccess: isBookingConnectionsFetchSuccess,
  } = useQuery({
    queryKey: ['bookingConnections', filters],
    queryFn: async () => {
      const response = await axios.get('/dashboards/booking_connections', {
        params: {
          page: selectedBookingConnectionsPage[selectedTabKey],
          date_filter: selectedTab.param,
          search: currentSearch.bookingSearch,
          e_learning: selectedTab.param === 'e_learning',
        },
      });
      return response.data;
    },
  });

  const selectedBookingConnection =
    selectedBookingConnectionId &&
    bookingConnections?.data.find(
      (bookingConnection) =>
        bookingConnection.id === selectedBookingConnectionId,
    );

  const selectedBookingSource = (function getSelectedBookingSource() {
    if (!selectedBookingConnection) return null;
    const { id, type } = selectedBookingConnection.relationships.source.data;
    return bookingConnections.included.find(
      (inclusion) => inclusion.type === type && inclusion.id === id,
    );
  })();

  const selectedBookerId = selectedBookingSource?.relationships.booker.data?.id;

  const selectedDomainCourse = (() => {
    if (bookingSidePanelContext === 'new')
      return findCourseFromCache(queryClient, selectedCourseId);
    if (['show', 'edit'].includes(bookingSidePanelContext))
      return (
        selectedBookingSource &&
        bookingConnections.included.find(
          (inclusion) =>
            inclusion.id ===
              selectedBookingSource.relationships.course.data.id &&
            inclusion.type === 'course',
        )
      );
    return null;
  })();

  const selectedDomainELearningCourse = (() => {
    if (
      !selectedDomainCourse ||
      !selectedDomainCourse.relationships.eLearningCourse.data
    ) {
      return null;
    }
    if (bookingSidePanelContext === 'new')
      return selectedDomainCourse.relationships.eLearningCourse.data ?
          findELearningCourseFromCache(
            queryClient,
            selectedDomainCourse.relationships.eLearningCourse.data.id,
          )
        : null;
    if (['show', 'edit'].includes(bookingSidePanelContext))
      return bookingConnections.included.find(
        (inclusion) =>
          inclusion.id ===
            selectedDomainCourse.relationships.eLearningCourse.data.id &&
          inclusion.type === 'eLearningCourse',
      );
    return null;
  })();

  const personnelListContext = (() => {
    if (bookingSidePanelIsOpen) {
      return `booking_${bookingSidePanelContext}`;
    } else if (bookingConnectionTrainingSidePanelIsOpen) {
      return 'training_new';
    }
  })();

  const isDomainBookingGroupPersonnelFetchEnabled =
    !!selectedBookingSource &&
    selectedBookingSource.type === 'bookingGroup' &&
    !!personnelListContext;
  const domainPersonnelQueryKey = [
    'domainBookingGroupPersonnel',
    selectedBookingSource?.id,
    personnelListContext,
  ];

  const {
    data: domainBookingGroupPersonnel,
    fetchNextPage: handleFetchNextPageOfbookingGroupPersonnel,
    hasNextPage: hasNextPageOfbookingGroupPersonnel,
    isFetching: isFetchingBookingGroupPersonnel,
    isSuccess: isBookingGroupPersonnelFetchSuccess,
  } = useInfiniteQuery({
    queryKey: domainPersonnelQueryKey,
    queryFn: async ({ pageParam = 1 }) => {
      const areAllPersonnelNeeded = ['booking_edit', 'training_new'].includes(
        personnelListContext,
      );
      const params = { page: areAllPersonnelNeeded ? 'all' : pageParam };
      if (personnelListContext === 'booking_show') {
        params.with_booking_write_eligibility = true;
        params.with_reminder_eligibility = true;
        params.with_training_write_eligibility = true;
      } else if (personnelListContext === 'training_new') {
        params.only_training_eligible = true;
      }
      const response = await axios.get(
        `/booking_groups/${selectedBookingSource?.id}/personnel`,
        { params },
      );
      return response.data;
    },
    getNextPageParam,
    select: (data) => formatInfiniteData({ data }),
    onSuccess: (personnel) => {
      const personnelIds = personnel.data.map((person) => person.id);
      setCurrentBookingConnection({
        ...currentBookingConnection,
        personnelIds,
      });
      setCurrentBookingConnectionTraining({
        ...currentBookingConnectionTraining,
        personnelIds,
      });
    },
    enabled: isDomainBookingGroupPersonnelFetchEnabled,
  });

  const loadMore =
    !isFetchingBookingGroupPersonnel &&
    hasNextPageOfbookingGroupPersonnel &&
    isEndOfDomainPersonnelListRefInView;
  if (loadMore) handleFetchNextPageOfbookingGroupPersonnel();

  const { data: eLearningAllowance, isError: isELearningAllowanceError } =
    useQuery({
      queryKey: ['eLearningAllowance', selectedDomainELearningCourse],
      queryFn: async () => {
        const response = await axios.get('/e_learning/allowance');
        return response.data;
      },
      enabled:
        currentActor.isAllowedFeature('e_learning') &&
        !!selectedDomainELearningCourse &&
        !selectedDomainELearningCourse.attributes.custom,
      meta: {
        errorMessage: i18nTCommon('bookings.errors.creditInfoUnavailable'),
      },
      retry: false,
    });

  const attachmentIdsToCheck = currentBookingConnectionTraining.attachmentIds;

  const {
    data: attachmentsBulkProcessingStatus,
    isError: isAttachmentsBulkProcessingStatusFetchError,
  } = useAttachmentsBulkProcessingStatusQuery({
    attachmentIds: attachmentIdsToCheck,
    enabled: bookingConnectionTrainingSidePanelIsOpen,
    queryOptions: {
      meta: {
        errorMessage: i18nT('errors.evidenceProcessingStatusUnavailable'),
      },
    },
  });

  const attachmentsAreProcessing =
    attachmentIdsToCheck.length > 0 &&
    !isAttachmentsBulkProcessingStatusFetchError &&
    !attachmentsBulkProcessingStatus?.data?.attributes?.processed;

  const createBookingMutation = useMutation({
    mutationFn: async ({ booking, personnelId }) => {
      const response = await axios.post(`/personnel/${personnelId}/bookings`, {
        booking,
      });
      return response.data;
    },
    onSuccess: () => {
      invalidateQueries({
        queryKeys: [
          ['assignableBookees'],
          ['bookingConnections'],
          ['bookingGroupPersonnelPages'],
          ['domainBookingGroupPersonnel'],
        ],
      });
    },
    onSettled: () => invalidateQueries({ queryKeys: [['eLearningAllowance']] }),
    onError: handleRequestError,
  });

  const createBookingGroupMutation = useMutation({
    mutationFn: async (newBookingGroup) => {
      const response = await axios.post(`/booking_groups`, newBookingGroup);
      return response.data;
    },
    onSuccess: () => {
      invalidateQueries({
        queryKeys: [
          ['assignableBookees'],
          ['bookingConnections'],
          ['bookingGroupPersonnelPages'],
          ['domainBookingGroupPersonnel'],
        ],
      });
    },
    onSettled: () => invalidateQueries({ queryKeys: [['eLearningAllowance']] }),
    onError: handleRequestError,
  });

  const updateBookingConnectionMutation = useMutation({
    mutationFn: async (bookingConnectionToBeUpdated) => {
      const response = await axios.patch(
        `/dashboards/booking_connections/${bookingConnectionToBeUpdated.id}`,
        bookingConnectionToBeUpdated,
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateQueries({
        queryKeys: [
          ['assignableBookees'],
          ['bookingConnections'],
          ['bookingGroupPersonnelPages'],
          ['domainBookingGroupPersonnel'],
        ],
      });
    },
    onSettled: () => invalidateQueries({ queryKeys: [['eLearningAllowance']] }),
    onError: handleRequestError,
  });

  const createBookingConnectionChangeNotificationMutation = useMutation({
    mutationFn: async ({
      bookingConnectionId,
      personnelIds,
      sourceId,
      sourceType,
    }) => {
      const response = await axios.post(
        `/booking_connections/${bookingConnectionId}/change_notifications`,
        {
          personnel_ids: personnelIds,
          source_id: sourceId,
          source_type: sourceType,
        },
      );

      return response;
    },
    onError: breadBoard.addInedibleToast,
  });

  const destroyBookingMutation = useMutation({
    mutationFn: async ({ bookingId, personnelId }) => {
      const response = await axios.delete(
        `/personnel/${personnelId}/bookings/${bookingId}`,
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateQueries({
        queryKeys: [
          ['assignableBookees'],
          ['bookingConnections'],
          ['bookingGroupPersonnelPages'],
          ['domainBookingGroupPersonnel'],
        ],
      });
    },
    onError: breadBoard.addInedibleToast,
  });

  const destroyBookingGroupMutation = useMutation({
    mutationFn: async ({ bookingGroup }) => {
      const response = await axios.delete(`/booking_groups/${bookingGroup.id}`);
      return response.data;
    },
    onSuccess: () => {
      invalidateQueries({
        queryKeys: [
          ['assignableBookees'],
          ['bookingConnections'],
          ['bookingGroupPersonnelPages'],
          ['domainBookingGroupPersonnel'],
        ],
      });
    },
    onError: breadBoard.addInedibleToast,
  });

  const createBulkTrainingMutation = useMutation({
    mutationFn: async ({ courseId, trainingParams }) => {
      const response = await axios.post(
        `/courses/${courseId}/trainings_bulk_creations`,
        trainingParams,
      );
      return response.data;
    },
    onSuccess: (bookingConnectionTraining) => {
      invalidateQueries({
        queryKeys: [
          ['assignableBookees'],
          ['bookingConnections'],
          ['bookingGroupPersonnelPages'],
        ],
      });
      // Recording training can empty a booking group, which the server then destroys - refetching here would request it
      queryClient.invalidateQueries({
        queryKey: ['domainBookingGroupPersonnel'],
        refetchType: 'none',
      });
      return bookingConnectionTraining;
    },
    onError: handleRequestError,
  });

  const createBulkEnrolmentMutation = useMutation({
    mutationFn: async ({ courseId, enrolmentParams }) => {
      const response = await axios.post(
        `/courses/${courseId}/enrolments_bulk_creations`,
        enrolmentParams,
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateQueries({
        queryKeys: [
          ['assignableBookees'],
          ['bookingConnections'],
          ['bookingGroupPersonnelPages'],
          ['domainBookingGroupPersonnel'],
        ],
      });
    },
    onSettled: () => invalidateQueries({ queryKeys: [['eLearningAllowance']] }),
    onError: handleRequestError,
  });

  function invalidateQueries({ queryKeys }) {
    queryKeys.forEach((queryKey) => queryClient.invalidateQueries(queryKey));
  }

  function handleBulkEnrolmentSubmit() {
    const createParams = {
      courseId: selectedDomainCourse.id,
      enrolmentParams: {
        enrolment_bulk_creation: {
          personnel_ids: currentBookingConnection.personnelIds,
        },
      },
    };

    createBulkEnrolmentMutation.mutate(createParams, {
      onSuccess: (data, variables) => {
        const course = data.included.find(
          (inclusion) => inclusion.type === 'course',
        );

        breadBoard.addToast(
          <SuccessToast
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey='bookings.BookingsTab.handleBulkEnrolmentSubmit.success'
                ns='components'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{
                  count:
                    variables.enrolmentParams.enrolment_bulk_creation
                      .personnel_ids.length,
                  courseName: course.attributes.name,
                  date: formatDate({
                    date: new Date(),
                    format: 'd MMM, yyyy',
                  }),
                }}
              />
            }
            onBurnToast={breadBoard.handleBurnToast}
          />,
        );

        handleCloseBookingSidePanel();
        resetBookingConnections();
      },
    });
  }

  function handleBookingCreateSubmit() {
    const isCreatingSingularBooking =
      currentBookingConnection.personnelIds.length === 1;

    if (!isCreatingSingularBooking && selectedDomainELearningCourse) {
      handleBulkEnrolmentSubmit();
      return;
    }

    const bookingSourceCreationMutation =
      isCreatingSingularBooking ?
        createBookingMutation
      : createBookingGroupMutation;

    const bookingDetails = {
      course_id: selectedDomainCourse.id,
      date: moment
        .parseZone(currentBookingConnection.date)
        .format('DD/MM/YYYY'),
      notes: currentBookingConnection.notes,
    };

    const creationParams =
      isCreatingSingularBooking ?
        {
          personnelId: currentBookingConnection.personnelIds[0],
          booking: { ...bookingDetails },
        }
      : {
          booking_group: {
            ...bookingDetails,
            personnel_ids: currentBookingConnection.personnelIds,
          },
        };

    bookingSourceCreationMutation.mutate(creationParams, {
      onSuccess: (data, variables) => {
        const courseName = selectedDomainCourse.attributes.name;

        breadBoard.addToast(
          <SuccessToast
            message={
              data.data.type === 'booking' ?
                <Trans
                  components={{ span: <span className='tw-font-medium' /> }}
                  i18nKey={
                    selectedDomainELearningCourse ?
                      'personnel.enrolledOnCourse'
                    : 'personnel.bookedOnCourse'
                  }
                  ns='common'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    courseName,
                    firstName: data.included.find(
                      (inclusion) => inclusion.type === 'personnel',
                    ).attributes.firstName,
                  }}
                />
              : <Trans
                  components={{ span: <span className='tw-font-medium' /> }}
                  i18nKey='bookings.BookingsTab.handleBookingCreateSubmit.success.bookingGroup'
                  ns='components'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    count: variables.booking_group.personnel_ids.length,
                    courseName,
                    date: formatDate({
                      date: data.data.attributes.date,
                      format: 'd MMM, yyyy',
                    }),
                  }}
                />
            }
            onBurnToast={breadBoard.handleBurnToast}
          />,
        );

        handleCloseBookingSidePanel();
        resetBookingConnections();
      },
    });
  }

  function handleUpdateBookingConnectionSubmit() {
    if (currentBookingConnection.personnelIds.length === 0) {
      setIsRemoveBookingConnectionModalOpen(true);
      return;
    }

    updateBookingConnectionMutation.mutate(
      {
        id: currentBookingConnection.id,
        booking_connection: {
          source_attributes: {
            date:
              moment.parseZone(currentBookingConnection.date).isValid() ?
                moment
                  .parseZone(currentBookingConnection.date)
                  .format('DD/MM/YYYY')
              : null, // Moment returns 'Invalid Date' if currentBookingConnection.date is not set.
            notes: currentBookingConnection.notes,
          },
          personnel_ids: currentBookingConnection.personnelIds,
          source_id: selectedBookingSource.id,
          source_type: selectedBookingSource.type,
        },
      },
      {
        onSuccess: (data) => {
          handleCloseBookingSidePanel();

          if (data.meta.hasBookingDetailsChanged) {
            new Promise((resolve) => {
              setTimeout(() => {
                resolve();
              }, 500);
            }).then(() =>
              setIsSendBookingConnectionUpdatedNotificationModalOpen(true),
            ); // might be able to use advanced timers
          } else {
            addUpdateToast({
              onlyChangedNotified: true,
              updatedBookingConnection: data,
            });
            resetBookingConnections();
          }
        },
      },
    );
  }

  function handleUpdateBookingNotification() {
    const updatedBookingConnection = updateBookingConnectionMutation.data;

    createBookingConnectionChangeNotificationMutation.mutate(
      {
        bookingConnectionId: updatedBookingConnection.data.id,
        personnelIds: updatedBookingConnection.meta.unchangedPersonnelIds,
        sourceId: updatedBookingConnection.data.relationships.source.data.id,
        sourceType:
          updatedBookingConnection.data.relationships.source.data.type,
      },
      {
        onSuccess: () => {
          addUpdateToast({
            onlyChangedNotified: false,
            updatedBookingConnection,
          });

          resetBookingConnections();
          setIsSendBookingConnectionUpdatedNotificationModalOpen(false);
        },
      },
    );
  }

  function handleDestroyBookingSourceSubmit({ bookingSource, course }) {
    const isBookingGroup = bookingSource.type === 'bookingGroup';
    const destroyParams =
      isBookingGroup ?
        { bookingGroup: bookingSource }
      : {
          personnelId: bookingSource.relationships.personnel.data.id,
          bookingId: bookingSource.id,
        };

    (isBookingGroup ?
      destroyBookingGroupMutation
    : destroyBookingMutation
    ).mutate(destroyParams, {
      onSuccess: (data) => {
        setIsRemoveBookingConnectionModalOpen(false);
        handleCloseBookingSidePanel();

        const doesCourseHaveELearningCourse =
          !!course?.relationships?.eLearningCourse?.data?.id;

        breadBoard.addToast(
          <SuccessToast
            message={
              isBookingGroup ?
                <Trans
                  components={{ span: <span className='tw-font-medium' /> }}
                  i18nKey='bookings.BookingsTab.handleDestroyBookingSourceSubmit.success.bookingGroup'
                  ns='components'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    count: bookingSource.meta.personnelCount,
                    courseName: course.attributes.name,
                    date: formatDate({
                      date: new Date(),
                      format: 'd MMM, yyyy',
                    }),
                  }}
                />
              : <Trans
                  components={{ span: <span className='tw-font-medium' /> }}
                  i18nKey={
                    doesCourseHaveELearningCourse ?
                      'bookings.BookingsTab.handleDestroyBookingSourceSubmit.success.eLearning'
                    : 'bookings.removedForCourse'
                  }
                  ns={doesCourseHaveELearningCourse ? 'components' : 'common'}
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    courseName: course.attributes.name,
                    firstName: data.included.find(
                      (inclusion) => inclusion.type === 'personnel',
                    ).attributes.firstName,
                  }}
                />
            }
            onBurnToast={breadBoard.handleBurnToast}
          />,
        );
      },
    });
  }

  const handleUpdateNotificationModalCancel = () => {
    addUpdateToast({
      onlyChangedNotified: true,
      updatedBookingConnection: updateBookingConnectionMutation.data,
    });

    resetBookingConnections();
    setIsSendBookingConnectionUpdatedNotificationModalOpen(false);
  };

  function handleEditBookingConnectionClick() {
    const personnelIds = [];
    if (selectedBookingSource.type === 'booking') {
      personnelIds.push(selectedBookingSource.relationships.personnel.data.id);
    } else {
      invalidateQueries({
        queryKeys: [
          [
            'domainBookingGroupPersonnel',
            selectedBookingSource.id,
            'booking_edit',
          ],
        ],
      });
    }

    setCurrentBookingConnection({
      id: selectedBookingConnection.id,
      date: moment(selectedBookingSource.attributes.date).toDate(),
      notes: selectedBookingSource.attributes.notes,
      personnelIds,
    });

    setBookingSidePanelContext('edit');
  }

  function handleCourseOptionChange({ courseOptionId }) {
    const bookingCourse = findCourseFromCache(queryClient, courseOptionId);
    setSelectedCourseId(courseOptionId);
    setCurrentBookingConnection({
      ...currentBookingConnection,
      notes: bookingCourse?.attributes?.defaultBookingNote,
    });
    setBookingSidePanelContext('new');
    openBookingSidePanel();
  }

  function handleBookingGroupMemberSelect(personnelId) {
    if (
      _.isEqual(requestError.validationErrors.personnel?.error.types, [
        'required',
      ])
    ) {
      removeErrorStyling({ target: { name: 'personnel' } });
    }

    const newPersonnelIds = [
      personnelId,
      ...currentBookingConnection.personnelIds,
    ];

    setCurrentBookingConnection({
      ...currentBookingConnection,
      personnelIds: newPersonnelIds,
    });
  }

  function handleBookingGroupMemberDelete(personnelId) {
    if (
      _.isEqual(requestError.validationErrors.personnel?.error.types, [
        'tracked_limit_reached',
      ])
    ) {
      removeErrorStyling({ target: { name: 'personnel' } });
    }

    const newPersonnelIds = currentBookingConnection.personnelIds.filter(
      (id) => id !== personnelId,
    );

    setCurrentBookingConnection({
      ...currentBookingConnection,
      personnelIds: newPersonnelIds,
    });
  }

  function handleCloseBookingSidePanel() {
    resetRequestError();
    resetBookingSidePanelContext();
    closeBookingSidePanel();
  }

  function handleBookingSidePanelCancel() {
    resetBookingConnections();
    handleCloseBookingSidePanel();
  }

  function resetBookingConnections() {
    setCurrentBookingConnection(defaultBookingConnection);
    setCurrentBookingConnectionTraining(defaultBookingConnectionTraining);
    setSelectedBookingConnectionId(null);
  }

  function handleViewBookingClick(bookingConnectionId) {
    setSelectedBookingConnectionId(bookingConnectionId);
    setBookingSidePanelContext('show');
    resetRequestError();
    openBookingSidePanel();
  }

  function handleRecordTrainingClick() {
    const personnelIds = [];
    if (selectedBookingSource.type === 'booking') {
      personnelIds.push(selectedBookingSource.relationships.personnel.data.id);
    } else {
      invalidateQueries({
        queryKeys: [
          [
            'domainBookingGroupPersonnel',
            selectedBookingSource.id,
            'training_new',
          ],
        ],
      });
    }

    const startDate = defaultTrainingStartDate({
      booking: selectedBookingSource,
    });

    const expiryDate =
      startDate ?
        calculateTrainingExpiryDate({ course: selectedDomainCourse, startDate })
      : null;

    setCurrentBookingConnectionTraining({
      ...currentBookingConnectionTraining,
      expiryDate,
      startDate,
      personnelIds,
    });

    handleCloseBookingSidePanel();
    openBookingConnectionTrainingSidePanel();
  }

  function handleBookingConnectionTrainingMemberDelete(personnelId) {
    const newPersonnelIds =
      currentBookingConnectionTraining.personnelIds.filter(
        (id) => id !== personnelId,
      );
    setCurrentBookingConnectionTraining({
      ...currentBookingConnectionTraining,
      personnelIds: newPersonnelIds,
    });
  }

  function resetBookingConnectionsTrainingSidePanel() {
    closeBookingConnectionTrainingSidePanel();
    resetBookingConnections();
    resetRequestError();
  }

  function handleBookingConnectionTrainingCalendarClose(calendarName) {
    setCurrentBookingConnectionTraining({
      ...currentBookingConnectionTraining,
      ...inferredTrainingExpiryDates({
        domainCourse: selectedDomainCourse,
        expiryDates: {
          startDate: currentBookingConnectionTraining.startDate,
          expiryDate: currentBookingConnectionTraining.expiryDate,
        },
        dateToInferFrom: calendarName,
      }),
    });
  }

  function handleCreateBookingConnectionTrainingSubmit() {
    createBulkTrainingMutation.mutate(
      {
        courseId: selectedDomainCourse.id,
        trainingParams: {
          bulk_training_creation: {
            expiry_date: moment
              .parseZone(currentBookingConnectionTraining.expiryDate)
              .format('DD/MM/YYYY'),
            notes: currentBookingConnectionTraining.notes,
            start_date: moment
              .parseZone(currentBookingConnectionTraining.startDate)
              .format('DD/MM/YYYY'),
            personnel_ids: currentBookingConnectionTraining.personnelIds,
            attachment_ids: currentBookingConnectionTraining.attachmentIds,
          },
        },
      },
      {
        onSuccess: (data) => {
          breadBoard.addToast(
            <SuccessToast
              message={
                <Trans
                  components={{ span: <span className='tw-font-medium' /> }}
                  i18nKey='bookings.BookingsTab.handleCreateBookingConnectionTrainingSubmit.success'
                  ns='components'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    count: data.meta.trainingCreationCount,
                    courseName: selectedDomainCourse.attributes.name,
                  }}
                />
              }
              onBurnToast={breadBoard.handleBurnToast}
            />,
          );

          resetBookingConnectionsTrainingSidePanel();
        },
      },
    );
  }

  function addUpdateToast({ onlyChangedNotified, updatedBookingConnection }) {
    const {
      changedPersonnelCount,
      hasBookingDetailsChanged,
      unchangedPersonnelIds,
    } = updatedBookingConnection.meta;

    const bookingSourceRelation =
      updatedBookingConnection.data.relationships.source.data;

    const bookingSource = updatedBookingConnection.included.find(
      (inclusion) =>
        inclusion.type === bookingSourceRelation.type &&
        inclusion.id === bookingSourceRelation.id,
    );

    const course = updatedBookingConnection.included.find(
      (inclusion) =>
        inclusion.type === 'course' &&
        inclusion.id === bookingSource.relationships.course.data.id,
    );

    const notifiedPersonnelCount =
      onlyChangedNotified ?
        changedPersonnelCount
      : unchangedPersonnelIds.length + changedPersonnelCount;

    breadBoard.addToast(
      <SuccessToast
        message={
          <>
            <Trans
              components={{ span: <span className='tw-font-medium' /> }}
              i18nKey={`bookings.BookingsTab.addUpdateToast.${
                hasBookingDetailsChanged ?
                  course.relationships.eLearningCourse.data ?
                    'eLearning'
                  : 'booking'
                : 'personnel'
              }`}
              ns='components'
              tOptions={{ interpolation: { escapeValue: false } }}
              values={{
                courseName: course.attributes.name,
                date: formatDate({ date: new Date(), format: 'd MMM, yyyy' }),
              }}
            />
            {notifiedPersonnelCount > 0 && (
              <>
                <br />
                <br />
                {i18nT('addUpdateToast.notifiedPersonnel', {
                  count: notifiedPersonnelCount,
                  interpolation: { escapeValue: false },
                })}
              </>
            )}
          </>
        }
        onBurnToast={breadBoard.handleBurnToast}
      />,
    );
  }

  function handleSegmentItemClick(tabName) {
    setSelectedTabKey(
      Object.keys(tabKeysAndNames).find(
        (key) => tabKeysAndNames[key] === tabName,
      ),
    );
  }

  function handlePageChange(event) {
    setSelectedBookingConnectionsPage({
      ...selectedBookingConnectionsPage,
      [selectedTabKey]: event.target.getAttribute('data-page'),
    });
  }

  function handleSearchReset() {
    setCurrentSearch({ ...currentSearch, bookingSearch: '' });
    setSelectedBookingConnectionsPage({
      ...selectedBookingConnectionsPage,
      [selectedTabKey]: '1',
    });
  }

  function handleBookingTabSelection(tabKey) {
    setSelectedTabKey(tabKey);
  }

  const handleBookingReminderClick = () => {
    openBookingSourceReminderModal();
  };

  function handleReminderModalClose() {
    setCurrentBookingConnection(defaultBookingConnection);
    closeBookingSourceReminderModal();
  }

  const sendBookingGroupReminderMutation = useMutation({
    mutationFn: async ({ bookingSource }) => {
      const response = await axios.post(
        `/booking_groups/${bookingSource.id}/reminders`,
      );
      return response.data;
    },
    onSuccess: () => invalidateQueries({ queryKeys: ['bookingConnections'] }),
    onError: breadBoard.addInedibleToast,
  });

  const sendBookingReminderMutation = useMutation({
    mutationFn: async ({ bookingSource }) => {
      const response = await axios.post(
        `/bookings/${bookingSource.id}/reminders`,
      );
      return response.data;
    },
    onSuccess: () => invalidateQueries({ queryKeys: ['bookingConnections'] }),
    onError: breadBoard.addInedibleToast,
  });

  function handleSendReminderSubmit({ bookingSource }) {
    handleReminderModalClose();
    (bookingSource.type === 'bookingGroup' ?
      sendBookingGroupReminderMutation
    : sendBookingReminderMutation
    ).mutate(
      { bookingSource },
      {
        onSuccess: (data) => {
          breadBoard.addToast(
            <SuccessToast
              message={(() => {
                if (bookingSource.type === 'bookingGroup') {
                  const bookingGroup = data.included.find(
                    (inclusion) =>
                      inclusion.type === 'bookingGroup' &&
                      inclusion.id ===
                        data.data.relationships.bookingGroup.data.id,
                  );
                  const course = data.included.find(
                    (inclusion) =>
                      inclusion.type === 'course' &&
                      inclusion.id ===
                        bookingGroup.relationships.course.data.id,
                  );
                  return (
                    <Trans
                      components={{ span: <span className='tw-font-medium' /> }}
                      i18nKey='bookings.BookingsTab.handleSendReminderSubmit.success.bookingGroup'
                      ns='components'
                      tOptions={{ interpolation: { escapeValue: false } }}
                      values={{
                        count:
                          domainBookingGroupPersonnel?.meta.notifiableSetCount,
                        courseName: course.attributes.name,
                        date: formatDate({
                          date: bookingGroup.attributes.notifiedAt,
                          format: 'd MMM, yyyy',
                        }),
                      }}
                    />
                  );
                } else {
                  const booking = data.included.find(
                    (inclusion) =>
                      inclusion.type === 'booking' &&
                      inclusion.id === data.data.relationships.booking.data.id,
                  );
                  const course = data.included.find(
                    (inclusion) =>
                      inclusion.type === 'course' &&
                      inclusion.id === booking.relationships.course.data.id,
                  );
                  const personnel = data.included.find(
                    (inclusion) =>
                      inclusion.type === 'personnel' &&
                      inclusion.id === booking.relationships.personnel.data.id,
                  );
                  return (
                    <Trans
                      components={{ span: <span className='tw-font-medium' /> }}
                      i18nKey='personnel.emailNotifications.personnelCourseReminder'
                      ns='common'
                      tOptions={{ interpolation: { escapeValue: false } }}
                      values={{
                        courseName: course.attributes.name,
                        firstName: personnel.attributes.firstName,
                      }}
                    />
                  );
                }
              })()}
              onBurnToast={breadBoard.handleBurnToast}
            />,
          );
          handleCloseBookingSidePanel();
        },
      },
    );
  }

  const {
    data: individualBookingPersonnel,
    isFetching: isFetchingIndividualBookingPersonnel,
    isSuccess: isIndividualBookingPersonnelSuccess,
  } = useQuery({
    queryKey: ['booking', selectedBookingSource?.id, 'personnel'],
    queryFn: async () => {
      const response = await axios.get(
        `/bookings/${selectedBookingSource?.id}/personnel`,
      );
      return response.data;
    },
    enabled: selectedBookingSource?.type === 'booking',
  });

  useEffect(() => {
    if (isBookingConnectionsFetchSuccess)
      setStore({
        currentTabPages: selectedBookingConnectionsPage,
        currentSearch: currentSearch.bookingSearch,
        selectedTabKey,
      });
  }, [
    isBookingConnectionsFetchSuccess,
    bookingConnections?.meta?.currentPage,
    currentSearch.bookingSearch,
    selectedTabKey,
  ]);

  useEffect(() => {
    if (debouncedCurrentSearch.bookingSearch) {
      setSelectedBookingConnectionsPage({
        ...selectedBookingConnectionsPage,
        [selectedTabKey]: '1',
      });
    }
  }, [debouncedCurrentSearch.bookingSearch]);

  return (
    <TabContext.Provider
      value={{
        selectedTabKey,
        selectedTabName: tabKeysAndNames[selectedTabKey],
        tabKeys,
        tabNames,
        tabs,
      }}
    >
      <BookingsBar
        bookingSearch={currentSearch.bookingSearch}
        onCourseOptionChange={handleCourseOptionChange}
        onSearchInputChange={handleSearchInputChange}
        onSearchReset={handleSearchReset}
        onSegmentItemClick={handleSegmentItemClick}
      />
      {isBookingConnectionsFetchSuccess &&
        (bookingConnections.data.length > 0 ?
          <>
            <BookingsTable
              bookingConnections={bookingConnections}
              onRowClick={handleViewBookingClick}
            />
            {bookingConnections.meta.totalPages > 1 && (
              <div className='tw-mt-20 tw-text-center'>
                <Paginator
                  currentPage={bookingConnections.meta.currentPage}
                  onClick={handlePageChange}
                  totalPages={bookingConnections.meta.totalPages}
                />
              </div>
            )}
          </>
        : debouncedCurrentSearch.bookingSearch ?
          <BlankBookingSearchResult
            onBookingTabSelection={handleBookingTabSelection}
          />
        : <ResourceBlankNotice
            icon={selectedTab.icon}
            noResourcesDescription={i18nT(
              `blankNotice.noResourcesDescription.${selectedTabKey}`,
            )}
            noResourcesDescriptionClasses='tw-w-[700px] tw-mt-4 tw-mx-auto'
            noResourcesTitle={tabKeysAndNames[selectedTabKey]}
            noResourcesTitleAdditionalClasses='tw-mt-3.5'
            totalCount={0}
          />)}
      <BookingSidePanel
        currentBooking={currentBookingConnection}
        domainBooker={
          selectedBookerId ?
            bookingConnections.included.find(
              (inclusion) =>
                inclusion.id === selectedBookerId &&
                inclusion.type === 'simpleUser',
            )
          : null
        }
        domainBookingSource={selectedBookingSource}
        domainCourse={selectedDomainCourse}
        domainELearningCourse={selectedDomainELearningCourse}
        domainPersonnel={
          selectedBookingSource?.type === 'booking' ?
            individualBookingPersonnel?.data
          : domainBookingGroupPersonnel?.data
        }
        domainPersonnelFetchMeta={{
          isSuccess:
            selectedBookingSource?.type === 'booking' ?
              isIndividualBookingPersonnelSuccess
            : isBookingGroupPersonnelFetchSuccess,
          isFetching:
            selectedBookingSource?.type === 'booking' ?
              isFetchingIndividualBookingPersonnel
            : isFetchingBookingGroupPersonnel,
        }}
        domainPersonnelMeta={
          selectedBookingSource?.type === 'booking' ?
            individualBookingPersonnel?.data.meta
          : domainBookingGroupPersonnel?.meta
        }
        eLearningAllowance={eLearningAllowance}
        endOfDomainPersonnelListRef={endOfDomainPersonnelListRef}
        isELearningAllowanceError={isELearningAllowanceError}
        onBookingCreateSubmit={handleBookingCreateSubmit}
        onBookingGroupMemberDelete={handleBookingGroupMemberDelete}
        onBookingGroupMemberSelect={handleBookingGroupMemberSelect}
        onBookingReminderClick={handleBookingReminderClick}
        onBookingUpdateSubmit={handleUpdateBookingConnectionSubmit}
        onCancel={handleBookingSidePanelCancel}
        onDateChange={handleBookingConnectionDateChange}
        onEditBookingClick={handleEditBookingConnectionClick}
        onInputChange={handleBookingConnectionInputChange}
        onRecordTraining={handleRecordTrainingClick}
        onRemoveBooking={() => setIsRemoveBookingConnectionModalOpen(true)}
        removeErrorStyling={removeErrorStyling}
        requestError={requestError}
        showBookingGroupView={true}
        sidePanelContext={bookingSidePanelContext}
        sidePanelIsOpen={bookingSidePanelIsOpen}
        submitDisabled={
          submitDisabled ||
          createBookingGroupMutation.isLoading ||
          createBookingMutation.isLoading ||
          updateBookingConnectionMutation.isLoading
        }
      />
      {selectedDomainCourse && bookingConnectionTrainingSidePanelIsOpen && (
        <BookingConnectionTrainingSidePanel
          currentBookingConnectionTraining={currentBookingConnectionTraining}
          domainCourse={selectedDomainCourse}
          domainPersonnelCollection={
            selectedBookingSource?.type === 'booking' ?
              individualBookingPersonnel?.data && [
                { ...individualBookingPersonnel.data },
              ]
            : domainBookingGroupPersonnel?.data
          }
          onBookingGroupMemberDelete={
            handleBookingConnectionTrainingMemberDelete
          }
          onCalendarClose={handleBookingConnectionTrainingCalendarClose}
          onCancel={resetBookingConnectionsTrainingSidePanel}
          onDateChange={handleBookingConnectionTrainingDateChange}
          onInputChange={handleBookingConnectionTrainingInputChange}
          onSubmit={handleCreateBookingConnectionTrainingSubmit}
          removeErrorStyling={removeErrorStyling}
          requestError={requestError}
          sidePanelContext='new'
          sidePanelIsOpen={bookingConnectionTrainingSidePanelIsOpen}
          submitDisabled={
            submitDisabled ||
            createBulkTrainingMutation.isLoading ||
            attachmentsAreProcessing
          }
        />
      )}
      {isSendBookingConnectionUpdatedNotificationModalOpen && (
        <SendBookingConnectionUpdatedNotificationModal
          bookingConnectionUpdatedMutationData={
            updateBookingConnectionMutation.data
          }
          onClose={handleUpdateNotificationModalCancel}
          onSubmit={handleUpdateBookingNotification}
        />
      )}
      {bookingSourceReminderModalIsOpen &&
        selectedDomainCourse &&
        selectedBookingSource && (
          <BookingSourceReminderModal
            bookingSource={selectedBookingSource}
            course={selectedDomainCourse}
            domainPersonnelMeta={
              selectedBookingSource.type === 'bookingGroup' ?
                domainBookingGroupPersonnel?.meta
              : null
            }
            isOpen={bookingSourceReminderModalIsOpen}
            onClose={handleReminderModalClose}
            onSubmit={handleSendReminderSubmit}
            personnel={
              selectedBookingSource.type === 'booking' ?
                bookingConnections.included.find(
                  (inclusion) =>
                    inclusion.id ===
                      selectedBookingSource.relationships.personnel.data.id &&
                    inclusion.type === 'personnel',
                )
              : null
            }
          />
        )}
      {isRemoveBookingConnectionModalOpen &&
        selectedBookingSource &&
        selectedDomainCourse &&
        ['show', 'edit'].includes(bookingSidePanelContext) && (
          <DestroyBookingSourceModal
            bookingSource={selectedBookingSource}
            course={selectedDomainCourse}
            isOpen={isRemoveBookingConnectionModalOpen}
            onClose={() => setIsRemoveBookingConnectionModalOpen(false)}
            onDestroySubmit={handleDestroyBookingSourceSubmit}
          />
        )}
    </TabContext.Provider>
  );
}
